import React, { useCallback, useEffect, useRef, useState } from "react";
import PlaybackSpeedSelect from "./PlaybackSpeedSelect";
import "../../css/hosted-video-embed.css";
import {
  useRegisterSingleActiveMedia,
  useSingleActiveMedia,
} from "../../hooks/useSingleActiveMedia";

const ChatVideoPlayer = ({ videoUrl }) => {
  const { register, claimActive, releaseActive } = useSingleActiveMedia();
  const [showControls, setShowControls] = useState(true);
  const videoRef = useRef(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [isMuted, setIsMuted] = useState(false);
  const [progress, setProgress] = useState(0);
  const [duration, setDuration] = useState(0);
  const [currentTime, setCurrentTime] = useState(0);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const containerRef = useRef(null);
  const wasPlayingBeforeFullscreenRef = useRef(false);
  const [showSettingsMenu, setShowSettingsMenu] = useState(false);
  const [playbackRate, setPlaybackRate] = useState(1.0);
  const settingsMenuRef = useRef(null);

  const stopPlayback = useCallback(() => {
    if (videoRef.current && !videoRef.current.paused) {
      videoRef.current.pause();
    }
    setIsPlaying(false);
  }, []);

  useRegisterSingleActiveMedia(stopPlayback, register);

  // Keep playback speed in sync with state
  useEffect(() => {
    if (!videoRef.current) return;
    videoRef.current.playbackRate = playbackRate;
  }, [playbackRate, videoUrl]);

  const handlePlaybackSpeedChange = async (nextRate) => {
    const video = videoRef.current;
    const wasPlaying = video ? !video.paused && !video.ended : false;

    // Apply immediately
    if (video) {
      video.playbackRate = nextRate;
    }
    setPlaybackRate(nextRate);

    // If it was playing, try to keep it playing (some browsers can pause on rate change)
    if (video && wasPlaying) {
      try {
        await video.play();
      } catch (e) {
        // ignore (autoplay / gesture restrictions)
      }
    }
  };

  // Close settings when clicking outside or hitting ESC
  useEffect(() => {
    if (!showSettingsMenu) return;

    const onClick = (e) => {
      if (!settingsMenuRef.current) return;
      if (!settingsMenuRef.current.contains(e.target)) {
        setShowSettingsMenu(false);
      }
    };

    const onKeyDown = (e) => {
      if (e.key === "Escape") {
        setShowSettingsMenu(false);
      }
    };

    document.addEventListener("click", onClick);
    document.addEventListener("keydown", onKeyDown);
    return () => {
      document.removeEventListener("click", onClick);
      document.removeEventListener("keydown", onKeyDown);
    };
  }, [showSettingsMenu]);
  // Keep UI play state in sync with the actual media element
  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;

    const handlePlay = () => setIsPlaying(true);
    const handlePause = () => setIsPlaying(false);

    video.addEventListener("play", handlePlay);
    video.addEventListener("pause", handlePause);

    return () => {
      video.removeEventListener("play", handlePlay);
      video.removeEventListener("pause", handlePause);
    };
  }, [videoUrl]);

  // Video controls
  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;

    const handleTimeUpdate = () => {
      setCurrentTime(video.currentTime);
      setProgress((video.currentTime / video.duration) * 100);
    };

    const handleEnded = () => {
      setIsPlaying(false);
      setShowControls(true);
      releaseActive();
    };

    video.addEventListener("timeupdate", handleTimeUpdate);
    video.addEventListener("ended", handleEnded);

    return () => {
      video.removeEventListener("timeupdate", handleTimeUpdate);
      video.removeEventListener("ended", handleEnded);
    };
  }, [videoUrl, releaseActive]);

  const togglePlay = () => {
    const video = videoRef.current;
    if (!video) return;

    if (video.paused) {
      claimActive(video);
      video.play().catch((error) => {
        console.error("Video play failed:", error);
        setIsPlaying(false);
        releaseActive();
      });
    } else {
      video.pause();
      releaseActive();
    }
    setShowControls(true);
  };

  const toggleMute = () => {
    if (videoRef.current) {
      videoRef.current.muted = !videoRef.current.muted;
      setIsMuted(!isMuted);
    }
  };

  const handleSeek = (e) => {
    if (!videoRef.current) return;
    const rect = e.currentTarget.getBoundingClientRect();
    const pos = (e.clientX - rect.left) / rect.width;
    videoRef.current.currentTime = pos * videoRef.current.duration;
  };

  const formatTime = (time) => {
    const minutes = Math.floor(time / 60);
    const seconds = Math.floor(time % 60);
    return `${minutes}:${seconds < 10 ? "0" : ""}${seconds}`;
  };

  useEffect(() => {
    const resumeIfNeeded = () => {
      const video = videoRef.current;
      const container = containerRef.current;
      if (!video || !container) return;

      const isFs = document.fullscreenElement === container;
      setIsFullscreen(isFs);

      if (
        isFs &&
        wasPlayingBeforeFullscreenRef.current &&
        video.paused &&
        !video.ended
      ) {
        claimActive(video);
        video.play().catch(() => {});
      }
    };

    document.addEventListener("fullscreenchange", resumeIfNeeded);
    return () =>
      document.removeEventListener("fullscreenchange", resumeIfNeeded);
  }, [claimActive]);

  const toggleFullscreen = async () => {
    const container = containerRef.current;
    const video = videoRef.current;
    if (!container || !video) return;

    if (document.fullscreenElement === container) {
      document.exitFullscreen().catch((err) => {
        console.error(`Error exiting fullscreen: ${err.message}`);
      });
      return;
    }

    wasPlayingBeforeFullscreenRef.current = !video.paused && !video.ended;

    if (document.fullscreenElement) {
      try {
        await document.exitFullscreen();
      } catch {
        // ignore
      }
    }

    try {
      await container.requestFullscreen();
      if (wasPlayingBeforeFullscreenRef.current && video.paused) {
        claimActive(video);
        await video.play();
      }
    } catch (err) {
      console.error(`Error attempting fullscreen: ${err.message}`);
    }
  };

  return (
    <div
      ref={containerRef}
      className={`chat-video-player-shell${isFullscreen ? " chat-video-player-shell--fullscreen" : ""}`}
    >
      <video
        ref={videoRef}
        key={videoUrl}
        className="chat-video-player-shell__video"
        src={videoUrl}
        playsInline
        preload="metadata"
        onLoadedMetadata={() => {
          if (videoRef.current) {
            setDuration(videoRef.current.duration);
          }
        }}
      />

      {/* Video controls */}
      {showControls && (
        <div
          className="chat-video-player-controls"
          style={{
            position: "absolute",
            bottom: "0px",
            left: "0",
            right: "0",
            padding: "5px 5px",
            backgroundColor: "rgba(0, 0, 0, 0.6)",
            borderBottomLeftRadius: "20px",
            borderBottomRightRadius: "20px",
          }}
        >
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: "8px",
              marginBottom: "8px",
            }}
          >
            <button
              onClick={togglePlay}
              style={{
                background: "none",
                border: "none",
                cursor: "pointer",
                color: "white",
                padding: "5px",
              }}
            >
              {isPlaying ? (
                <svg
                  width="20"
                  height="20"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="white"
                  strokeWidth="2"
                >
                  <rect x="6" y="4" width="4" height="16" />
                  <rect x="14" y="4" width="4" height="16" />
                </svg>
              ) : (
                <svg
                  width="20"
                  height="20"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="white"
                  strokeWidth="2"
                >
                  <polygon points="5 3 19 12 5 21 5 3" />
                </svg>
              )}
            </button>

            <button
              onClick={toggleMute}
              style={{
                background: "none",
                border: "none",
                cursor: "pointer",
                color: "white",
                padding: "5px",
              }}
            >
              {isMuted ? (
                <img
                  src="/icons/mute.svg"
                  alt="Unmute"
                  style={{ width: "20px", height: "20px" }}
                />
              ) : (
                <img
                  src="/icons/unmute.svg"
                  alt="Mute"
                  style={{ width: "20px", height: "20px" }}
                />
              )}
            </button>

            <span
              style={{
                color: "white",
                fontSize: "12px",
                minWidth: "40px",
              }}
            >
              {formatTime(currentTime)}
            </span>
            <div
              style={{
                flex: 1,
                height: "4px",
                backgroundColor: "rgba(255,255,255,0.3)",
                borderRadius: "2px",
                cursor: "pointer",
              }}
              onClick={handleSeek}
            >
              <div
                style={{
                  width: `${progress}%`,
                  height: "100%",
                  backgroundColor: "#008CFF",
                  borderRadius: "2px",
                }}
              />
            </div>
            <span
              style={{
                color: "white",
                fontSize: "12px",
                minWidth: "40px",
              }}
            >
              {formatTime(duration)}
            </span>

            {/* Fullscreen Button */}
            <button
              onClick={toggleFullscreen}
              style={{
                background: "none",
                border: "none",
                cursor: "pointer",
                color: "white",
                padding: "5px",
              }}
            >
              {isFullscreen ? (
                // Exit fullscreen icon
                <svg
                  width="20"
                  height="20"
                  viewBox="0 0 24 24"
                  stroke="white"
                  strokeWidth="2"
                  fill="none"
                >
                  <path d="M9 9H5V5h4V3H3v6h6V9zM15 3v2h4v4h2V3h-6zM5 15H3v6h6v-2H5v-4zM19 19h-4v2h6v-6h-2v4z" />
                </svg>
              ) : (
                // Enter fullscreen icon
                <svg
                  width="20"
                  height="20"
                  viewBox="0 0 24 24"
                  stroke="white"
                  strokeWidth="2"
                  fill="none"
                >
                  <path d="M9 3H3v6h2V5h4V3zm6 0v2h4v4h2V3h-6zm6 12h-2v4h-4v2h6v-6zM5 15H3v6h6v-2H5v-4z" />
                </svg>
              )}
            </button>

            <PlaybackSpeedSelect
              value={playbackRate}
              onChange={handlePlaybackSpeedChange}
              showLabel={false}
              // label={`${playbackRate}x`}
              selectStyle={{
                width: "100%",
                backgroundColor: "rgba(0,0,0,0.35)",
              }}
            />
          </div>
        </div>
      )}
    </div>
  );
};

export default ChatVideoPlayer;
